import React from "react";
import { Box, Breadcrumbs, Link, Typography, Stack } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  action?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs,
  action,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: { xs: "flex-start", md: "center" },
      justifyContent: "space-between",
      gap: 1,
      mb: 1.25,
      minHeight: 28,
    }}
  >
    <Box minWidth={0}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 14 }} />}
          sx={{ mb: 0.25, "& .MuiBreadcrumbs-li": { fontSize: "0.75rem" } }}
        >
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            if (crumb.to && !isLast) {
              return (
                <Link
                  key={crumb.label}
                  component={RouterLink}
                  to={crumb.to}
                  underline="hover"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  {crumb.label}
                </Link>
              );
            }
            return (
              <Typography
                key={crumb.label}
                color={isLast ? "text.primary" : "text.secondary"}
                fontSize="0.75rem"
                fontWeight={isLast ? 600 : 400}
              >
                {crumb.label}
              </Typography>
            );
          })}
        </Breadcrumbs>
      )}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 650,
          fontSize: "1.0625rem",
          color: "text.primary",
          lineHeight: 1.25,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.15, fontSize: "0.8125rem", lineHeight: 1.35 }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    {action && (
      <Stack
        direction="row"
        spacing={0.75}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        sx={{ "& .MuiButton-root": { alignSelf: "center" } }}
      >
        {action}
      </Stack>
    )}
  </Box>
);

export default PageHeader;
